import { useCallback, useEffect, useState } from "react";
import { LayoutChangeEvent } from "react-native";
import {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { useBooleanState } from "./states";

type Props<T extends any> = {
  options: T[];
  collapsedVisibleOptions: number;
};

export default function useHideMoreView<T extends any>(props: Props<T>) {
  const { options, collapsedVisibleOptions } = props;
  const [firstRender, setFirstRender] = useState(true);
  const { state: expanded, setState } = useBooleanState(false);
  const listHeight = useSharedValue<number | null>(null);
  const expandedListHeight = useSharedValue<number | null>(null);
  const collapsedListHeight = useSharedValue<number | null>(null);
  useEffect(() => {
    return () => {
      setState(false);
      setFirstRender(true);
      expandedListHeight.value = null;
      collapsedListHeight.value = null;
    };
  }, [expandedListHeight, collapsedListHeight, setState]);
  const changeListHeight = useCallback(
    (toHeight: number) => {
      if (toHeight === 0) return;
      if (listHeight.value === null) {
        listHeight.value = toHeight;
      } else {
        listHeight.value = withTiming(toHeight, {
          duration: 300,
          easing: Easing.inOut(Easing.quad),
        });
      }
    },
    [listHeight],
  );
  const layoutChange = useCallback(
    (event: LayoutChangeEvent) => {
      const { height } = event.nativeEvent.layout;
      if (height === 0) return;
      if (expanded) {
        expandedListHeight.value = height;
      } else if (!expanded) {
        collapsedListHeight.value = height;
      }
      changeListHeight(height);
    },
    [changeListHeight, expanded, collapsedListHeight, expandedListHeight],
  );
  const toggleExpanded = useCallback(() => {
    setFirstRender(false);
    setState((prev) => {
      if (prev) {
        collapsedListHeight.value &&
          changeListHeight(collapsedListHeight.value);
      } else {
        expandedListHeight.value && changeListHeight(expandedListHeight.value);
      }
      return !prev;
    });
  }, [setState, changeListHeight, collapsedListHeight, expandedListHeight]);
  const animatedStyle = useAnimatedStyle(() => ({
    height: listHeight.value === null ? undefined : listHeight.value,
    overflow: "hidden",
  }));
  const visibleOptions = firstRender
    ? options.slice(0, collapsedVisibleOptions)
    : options;
  const hasMore = options.length > collapsedVisibleOptions;
  return {
    expanded,
    toggleExpanded,
    animatedStyle,
    layoutChange,
    visibleOptions,
    hasMore,
  };
}
